import type { RealEstateContact } from "../../@types/sharedSchema";

export interface LiveContactSnapshot {
  contact: RealEstateContact | null;
  loading: boolean;
  error: Error | null;
}

type ListenToContact = (
  userEmail: string,
  contactId: string,
  onContact: (contact: RealEstateContact | null) => void,
  onError: (error: Error) => void
) => () => void;

interface Entry {
  snapshot: LiveContactSnapshot;
  listeners: Set<() => void>;
  unsubscribe?: () => void;
  releaseTimer?: ReturnType<typeof setTimeout>;
}

const RESOLVING: LiveContactSnapshot = { contact: null, loading: true, error: null };
const DISABLED: LiveContactSnapshot = { contact: null, loading: false, error: null };
const keyFor = (userEmail: string, contactId: string): string => JSON.stringify([userEmail, contactId]);

/**
 * Shares a live DOCUMENT between mounted readers, including the sidebar → full-page
 * handoff. This is not a session cache: after the last reader leaves, the listener and
 * snapshot are discarded on the next tick. Deferring that release lets React's new
 * surface subscribe after the old surface's effect cleanup without a cold read.
 */
export function createLiveContactStore(listen: ListenToContact) {
  const entries = new Map<string, Entry>();

  return {
    read(userEmail: string | null | undefined, contactId: string | null | undefined): LiveContactSnapshot {
      if (!userEmail || !contactId) return DISABLED;
      return entries.get(keyFor(userEmail, contactId))?.snapshot ?? RESOLVING;
    },

    subscribe(userEmail: string, contactId: string, listener: () => void): () => void {
      const key = keyFor(userEmail, contactId);
      let entry = entries.get(key);
      const isNew = !entry;
      if (!entry) {
        entry = { snapshot: RESOLVING, listeners: new Set() };
        entries.set(key, entry);
      }
      const held = entry;
      if (held.releaseTimer !== undefined) {
        clearTimeout(held.releaseTimer);
        held.releaseTimer = undefined;
      }
      held.listeners.add(listener);

      if (isNew) {
        const publish = (snapshot: LiveContactSnapshot): void => {
          // Ignore a queued callback from a listener that has already been released.
          if (entries.get(key) !== held) return;
          held.snapshot = snapshot;
          held.listeners.forEach((notify) => notify());
        };
        const onError = (error: Error): void => publish({ contact: null, loading: false, error });
        try {
          held.unsubscribe = listen(
            userEmail,
            contactId,
            (contact) => publish({ contact, loading: false, error: null }),
            onError
          );
        } catch (error) {
          onError(error instanceof Error ? error : new Error(String(error)));
        }
      }

      return () => {
        if (!held.listeners.delete(listener) || held.listeners.size > 0) return;
        held.releaseTimer = setTimeout(() => {
          entries.delete(key);
          held.unsubscribe?.();
        }, 0);
      };
    }
  };
}
